import { useState, useCallback, useRef, useEffect, startTransition } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'

/* ============================================================
   SectionSortable — Drag-and-drop section reordering
   Uses native HTML5 Drag & Drop API (no dependencies)
   Supports expand/collapse for inline content editing
   ============================================================ */

export interface SectionItem {
  id: string
  section_key: string
  enabled: boolean
  display_order: number
}

interface SectionSortableProps {
  sections: SectionItem[]
  onReorder: (updates: Array<{ id: string; display_order: number }>) => void
  onToggle: (id: string, enabled: boolean) => void
  isReordering?: boolean
  isToggling?: boolean
  /** Currently expanded section key */
  expandedKey?: string | null
  /** Called when user clicks a section row (not the drag handle or toggle) */
  onSectionClick?: (sectionKey: string) => void
  /** Render prop for expanded content below a section row */
  renderExpanded?: (section: SectionItem) => React.ReactNode
}

const SECTION_META: Record<string, { label: string; icon: string; description: string }> = {
  header: { label: 'Header', icon: '🧭', description: 'Site navigation and branding' },
  hero: { label: 'Hero', icon: '🎯', description: 'Main banner with headline and CTA' },
  about: { label: 'About', icon: '📖', description: 'About your business' },
  services: { label: 'Services', icon: '✂️', description: 'Service menu and pricing' },
  team: { label: 'Team', icon: '👥', description: 'Your stylists and staff' },
  testimonials: { label: 'Testimonials', icon: '⭐', description: 'Client reviews and feedback' },
  contact: { label: 'Contact', icon: '📍', description: 'Location, hours, and contact info' },
  events: { label: 'Events', icon: '📅', description: 'Upcoming events and promotions' },
  gallery: { label: 'Gallery', icon: '🖼️', description: 'Photo gallery showcase' },
  faq: { label: 'FAQ', icon: '❓', description: 'Frequently asked questions' },
  looks: { label: 'Looks', icon: '💇', description: 'Look-to-Book gallery section' },
  footer: { label: 'Footer', icon: '🦶', description: 'Site footer with links and social' },
}

export default function SectionSortable({
  sections,
  onReorder,
  onToggle,
  isReordering = false,
  isToggling = false,
  expandedKey = null,
  onSectionClick,
  renderExpanded,
}: SectionSortableProps) {
  // Track last seen prop reference — if it changes externally, reset the working copy
  const prevSectionsRef = useRef(sections)
  const [items, setItems] = useState(sections)
  useEffect(() => {
    if (prevSectionsRef.current !== sections) {
      prevSectionsRef.current = sections
      startTransition(() => setItems(sections))
    }
  }, [sections])
  const [dragIdxState, setDragIdxState] = useState<number | null>(null)
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleDragStart = useCallback((e: React.DragEvent, idx: number) => {
    setDragIdxState(idx)
    setIsDragging(true)
    // Auto-collapse expanded section during drag
    if (onSectionClick && expandedKey) {
      onSectionClick('')  // collapse by passing empty string
    }
    // Set drag image opacity
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move'
    }
  }, [onSectionClick, expandedKey])

  const handleDragOver = useCallback(
    (e: React.DragEvent, idx: number) => {
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
      setDragOverIdx(idx)

      if (dragIdxState === null || dragIdxState === idx) return
      const newItems = [...items]
      const dragged = newItems.splice(dragIdxState, 1)[0]
      newItems.splice(idx, 0, dragged)
      setDragIdxState(idx)
      setItems(newItems)
    },
    [items, dragIdxState],
  )

  const handleDragEnd = useCallback(() => {
    setIsDragging(false)
    setDragOverIdx(null)
    setDragIdxState(null)
    // Fire reorder with new display_order values
    onReorder(items.map((s, i) => ({ id: s.id, display_order: i })))
  }, [items, onReorder])

  const meta = (key: string) => SECTION_META[key] || { label: key, icon: '📄', description: '' }

  return (
    <div className="space-y-2">
      {items.map((section, idx) => {
        const { label, icon, description } = meta(section.section_key)
        const isOver = dragOverIdx === idx && isDragging
        const isDraggedItem = isDragging && dragIdxState === idx
        const isExpanded = expandedKey === section.section_key
        return (
          <div key={section.id} className="rounded-xl overflow-hidden">
            <div
              draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              className={`
                flex items-center gap-4 p-4
                border transition-[background-color,border-color] duration-200
                ${isExpanded ? 'rounded-t-xl' : 'rounded-xl'}
                ${section.enabled
                  ? 'bg-white dark:bg-white/[0.03] border-zinc-200 dark:border-white/10 hover:bg-zinc-50 dark:hover:bg-white/[0.06] hover:border-zinc-300 dark:hover:border-white/15'
                  : 'bg-zinc-50 dark:bg-white/[0.01] border-zinc-100 dark:border-white/5 opacity-50'
                }
                ${isOver ? 'border-origin-accent/50 bg-origin-accent/5 scale-[1.01]' : ''}
                ${isDraggedItem ? 'opacity-60 shadow-2xl shadow-origin-accent/10 scale-[0.98]' : ''}
                ${isExpanded ? 'border-b-0 border-origin-accent/20 bg-zinc-50 dark:bg-white/[0.04]' : ''}
              `}
            >
              {/* Drag handle */}
              <div className="flex flex-col items-center gap-0.5 text-zinc-300 dark:text-white/20 select-none shrink-0 cursor-grab active:cursor-grabbing" aria-hidden>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="9" cy="6" r="1.5" />
                  <circle cx="15" cy="6" r="1.5" />
                  <circle cx="9" cy="12" r="1.5" />
                  <circle cx="15" cy="12" r="1.5" />
                  <circle cx="9" cy="18" r="1.5" />
                  <circle cx="15" cy="18" r="1.5" />
                </svg>
              </div>

              {/* Icon */}
              <span className="text-2xl select-none shrink-0">{icon}</span>

              {/* Info — clickable to expand/collapse */}
              <div
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => onSectionClick?.(isExpanded ? '' : section.section_key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSectionClick?.(isExpanded ? '' : section.section_key)
                  }
                }}
              >
                <div className="flex items-center gap-2">
                  {onSectionClick && (
                    isExpanded
                      ? <ChevronDown size={12} className="text-origin-accent/60 shrink-0" />
                      : <ChevronRight size={12} className="text-zinc-400 dark:text-white/30 shrink-0" />
                  )}
                  <p className="text-sm font-semibold text-text-primary">{label}</p>
                  <span className="px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-white/5 border border-zinc-200 dark:border-white/10 text-[10px] text-text-muted font-mono">
                    {section.section_key}
                  </span>
                </div>
                {description && (
                  <p className="text-xs text-text-muted mt-0.5 truncate">{description}</p>
                )}
              </div>

              {/* Order Badge */}
              <span className="text-xs text-text-muted font-mono tabular-nums shrink-0">
                #{idx + 1}
              </span>

              {/* Toggle */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onToggle(section.id, !section.enabled)
                }}
                disabled={isToggling}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none shrink-0 ${
                  section.enabled ? 'bg-origin-accent' : 'bg-zinc-200 dark:bg-white/10'
                }`}
                role="switch"
                aria-checked={section.enabled}
                aria-label={`Toggle ${label}`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white transition-transform duration-200 ${
                    section.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Expanded content (inline editor) */}
            {isExpanded && renderExpanded && (
              <div className="border border-t-0 border-origin-accent/20 rounded-b-xl">
                {renderExpanded(section)}
              </div>
            )}
          </div>
        )
      })}

      {/* Saving indicator */}
      {isReordering && (
        <div className="flex items-center gap-2 px-4 py-2 text-xs text-origin-accent">
          <div className="w-3 h-3 rounded-full border-2 border-origin-accent border-t-transparent animate-spin" />
          Saving new order…
        </div>
      )}
    </div>
  )
}
